/** @jsxImportSource @emotion/react */
import * as React from "react";
import type { Theme } from "@emotion/react";
import { IconChevronBold } from "atoms";

interface AccordionProps extends React.HTMLAttributes<HTMLDetailsElement> {
  heading: React.ReactNode;
}

const css = (theme: Theme) =>
  ({
    borderBottom: `1px solid ${theme.colors.fg}`,
    color: theme.colors.fg,
    paddingBottom: ".75rem",
    summary: {
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: ".5rem",
      padding: "1.5rem 0 .75rem",
      transition: "color 0.1s ease",
      "& > *": {
        transition: "transform 0.2s ease",
      },
      "&:hover, &:focus-visible": {
        color: theme.colors.primary,
        outline: 0,
      },
      "&:active :not(svg)": {
        transform: "translateX(2px) scale(1, 1.1)",
      },
      "& > h1, & > h2, & > h3": {
        margin: 0,
      },
      "& > svg": {
        flex: "0 0 auto",
        margin: 0,
        overflow: "visible",
        width: ".8rem",
      },
    },
    "&[open] summary > svg": {
      transform: "rotate(180deg)",
    },
    "& > div": {
      margin: "0 0 1.25rem 1.33rem",
      "& > :last-child": {
        marginBottom: 0,
      },
    },
  } as const);

function Accordion({ heading, children, ...props }: AccordionProps) {
  return (
    <details css={css} {...props}>
      <summary>
        <IconChevronBold />
        {heading}
      </summary>

      <div>{children}</div>
    </details>
  );
}

export { Accordion };
